import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Scaffold } from 'layouts';
import * as S from '../styles';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useToast } from 'components/ui/use-toast';
import { useHoraCertaImport } from 'hooks/api/useHoraCertaImport';
import type {
  ExtractedReferenceGroup,
  HoraCertaImportRow,
  ReferenceEntityType,
  ReferenceMatchResult,
  ReferenceMatchStatus,
} from 'contracts/HoraCertaImport';
import {
  extractReferenceGroups,
  getHoraCertaRowImportStatus,
  toHoraCertaImportRows,
  HoraCertaSpreadsheetValidationError,
  type HoraCertaRowImportStatus,
} from './adapter';
import {
  INVALID_SPREADSHEET_MESSAGE,
  readSpreadsheet,
  type SpreadsheetPreview,
} from './spreadsheet';
import { ENTITY_ORDER } from './constants';
import {
  getErrorDetails,
  getErrorMessage,
  isUnresolved,
  resolveDetailKey,
  selectionKey,
} from './helpers';
import type { EntityCategoryCount, MatchOption, ReferenceEntity } from './types';
import { HoraCertaHeader } from './components/HoraCertaHeader';
import { HoraCertaStepper } from './components/HoraCertaStepper';
import { HoraCertaFileStep } from './components/HoraCertaFileStep';
import { HoraCertaPreviewStep } from './components/HoraCertaPreviewStep';
import { HoraCertaMappingStep } from './components/HoraCertaMappingStep';
import { HoraCertaResultStep } from './components/HoraCertaResultStep';

export const HoraCertaImportPage: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const { toast } = useToast();
  const {
    matchReferences,
    findReferenceOptions,
    createMappings,
    updateMapping,
    importOrders,
    findPendingThirdOrders,
  } = useHoraCertaImport();

  const [step, setStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<SpreadsheetPreview | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [rows, setRows] = useState<HoraCertaImportRow[]>([]);
  const [matches, setMatches] = useState<ReferenceMatchResult[]>([]);
  const [searchOptionsByEntity, setSearchOptionsByEntity] = useState<
    Record<string, MatchOption[]>
  >({});
  const [selections, setSelections] = useState<Record<string, number>>({});
  const [activeEntity, setActiveEntity] = useState<ReferenceEntity>('carrier');
  const [isMatching, setIsMatching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [ignoredCount, setIgnoredCount] = useState(0);
  const [cancelledCount, setCancelledCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dialogErrorMessage, setDialogErrorMessage] = useState<string | null>(
    null
  );
  const [overwriteDialogOpen, setOverwriteDialogOpen] = useState(false);
  const [overwriteDialogMessage, setOverwriteDialogMessage] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [focusErrorKey, setFocusErrorKey] = useState<string | null>(null);
  const [pendingThirdOrders, setPendingThirdOrders] = useState<Set<string>>(
    new Set()
  );

  const reset = useCallback(() => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setSheetName('');
    setRows([]);
    setMatches([]);
    setSearchOptionsByEntity({});
    setSelections({});
    setActiveEntity('carrier');
    setIsMatching(false);
    setIsSubmitting(false);
    setImportedCount(null);
    setUpdatedCount(0);
    setIgnoredCount(0);
    setCancelledCount(0);
    setErrorMessage(null);
    setDialogErrorMessage(null);
    setOverwriteDialogOpen(false);
    setOverwriteDialogMessage('');
    setEditingKey(null);
    setEditingValue('');
    setSavingKey(null);
    setRowErrors({});
    setFocusErrorKey(null);
    setPendingThirdOrders(new Set());
  }, []);

  const clearRowError = useCallback((key: string) => {
    setRowErrors((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const registerFieldRef = useCallback(
    (key: string, element: HTMLInputElement | null) => {
      if (element) fieldRefs.current[key] = element;
      else delete fieldRefs.current[key];
    },
    []
  );

  const applyApiErrorDetails = useCallback(
    (error: unknown, orderedMatches: ReferenceMatchResult[]): boolean => {
      const details = getErrorDetails(error);
      if (!details.length) return false;
      const next: Record<string, string> = {};
      details.forEach(({ field, message }) => {
        const key = resolveDetailKey(field, orderedMatches);
        if (key) next[key] = message;
      });
      if (!Object.keys(next).length) return false;
      setRowErrors(next);
      return true;
    },
    []
  );

  const processFile = useCallback(
    async (selected: File) => {
      setErrorMessage(null);
      setDialogErrorMessage(null);
      if (!selected.name.toLowerCase().endsWith('.xlsx')) {
        const message = 'Arquivo inválido. Selecione uma planilha .xlsx.';
        setDialogErrorMessage(message);
        toast({ variant: 'destructive', title: 'Erro', description: message });
        return;
      }

      try {
        const result = await readSpreadsheet(selected);
        const thirdOrders = result.sheets.flatMap((sheet) =>
          sheet.rows
            .map((row) => String(row.Agendamento ?? '').trim())
            .filter(Boolean)
        );
        const pending = await findPendingThirdOrders(thirdOrders);
        setPendingThirdOrders(new Set(pending));
        setFile(selected);
        setPreview(result);
        setSheetName(result.selectedSheetName);
        setStep(2);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : INVALID_SPREADSHEET_MESSAGE;
        setDialogErrorMessage(message);
        toast({ variant: 'destructive', title: 'Erro', description: message });
      }
    },
    [findPendingThirdOrders, toast]
  );

  const currentSheet = useMemo(
    () =>
      preview?.sheets.find((sheet) => sheet.name === sheetName) ??
      preview?.sheets[0],
    [preview, sheetName]
  );

  const rowImportStatuses = useMemo<HoraCertaRowImportStatus[]>(
    () =>
      (currentSheet?.rows || []).map((row) =>
        getHoraCertaRowImportStatus(row, new Date(), pendingThirdOrders)
      ),
    [currentSheet, pendingThirdOrders]
  );
  const hasImportableRows = rowImportStatuses.some(
    (status) => status === 'importable'
  );

  const goToCorrespondence = useCallback(async () => {
    if (!currentSheet) return;
    setErrorMessage(null);
    setDialogErrorMessage(null);
    setIsMatching(true);
    try {
      const importRows = toHoraCertaImportRows(currentSheet.rows);
      const groups: ExtractedReferenceGroup[] = extractReferenceGroups(importRows);
      const result = groups.length ? await matchReferences(groups) : [];
      setRows(importRows);
      setMatches(result);
      setSelections({});
      setActiveEntity(ENTITY_ORDER[0]);
      setStep(3);
    } catch (error) {
      if (error instanceof HoraCertaSpreadsheetValidationError) {
        const message = error.issues
          .map((issue) => `Linha ${issue.row} — ${issue.field}: ${issue.message}`)
          .join('\n');
        setDialogErrorMessage(message);
      } else {
        setDialogErrorMessage(getErrorMessage(error));
      }
    } finally {
      setIsMatching(false);
    }
  }, [currentSheet, matchReferences]);

  const entityMatches = useMemo(
    () => matches.filter((match) => match.entityType === activeEntity),
    [matches, activeEntity]
  );

  useEffect(() => {
    if (!focusErrorKey) return;
    const element = fieldRefs.current[focusErrorKey];
    if (!element) return;
    element.focus();
    element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setFocusErrorKey(null);
  }, [focusErrorKey]);

  const optionsByEntity = useCallback(
    (entityType: ReferenceEntityType): MatchOption[] => {
      const options = new Map<number, MatchOption>();
      const addOption = (option: MatchOption) => {
        const current = options.get(option.id);
        options.set(option.id, current ? { ...current, ...option } : option);
      };
      (searchOptionsByEntity[entityType] || []).forEach(addOption);
      matches
        .filter((match) => match.entityType === entityType)
        .forEach((match) => {
          if (match.resolved) addOption(match.resolved);
          (match.candidates || []).forEach(addOption);
        });
      return Array.from(options.values()).sort((a, b) =>
        a.text.localeCompare(b.text)
      );
    },
    [matches, searchOptionsByEntity]
  );

  const searchReferenceOptions = useCallback(
    async (entityType: ReferenceEntityType, search: string) => {
      const found = await findReferenceOptions(entityType, search);
      setSearchOptionsByEntity((current) => {
        const options = new Map(
          (current[entityType] || []).map((option) => [option.id, option])
        );
        found.forEach((option) => options.set(option.id, option));
        return { ...current, [entityType]: Array.from(options.values()) };
      });
    },
    [findReferenceOptions]
  );

  const saveMappingEdit = useCallback(
    async (match: ReferenceMatchResult, key: string) => {
      if (!match.mappingId || !editingValue) return;
      setSavingKey(key);
      clearRowError(key);
      setErrorMessage(null);
      try {
        await updateMapping(match.mappingId, Number(editingValue));
        const option = optionsByEntity(match.entityType).find(
          (item) => item.id === Number(editingValue)
        );
        setMatches((current) =>
          current.map((item) =>
            selectionKey(item.entityType, item.normalizedValue) === key
              ? {
                  ...item,
                  status: 'mapped',
                  resolved: option
                    ? { id: option.id, text: option.text }
                    : item.resolved,
                }
              : item
          )
        );
        setEditingKey(null);
        setEditingValue('');
      } catch (error) {
        const details = getErrorDetails(error);
        setRowErrors((current) => ({
          ...current,
          [key]:
            details.map((detail) => detail.message).join(' ') ||
            getErrorMessage(error),
        }));
        setErrorMessage(getErrorMessage(error));
      } finally {
        setSavingKey(null);
      }
    },
    [updateMapping, editingValue, optionsByEntity, clearRowError]
  );

  const unresolvedMatches = useMemo(() => matches.filter(isUnresolved), [matches]);

  const hasLocalSelection = useCallback(
    (match: ReferenceMatchResult) =>
      Boolean(selections[selectionKey(match.entityType, match.normalizedValue)]),
    [selections]
  );

  const effectiveStatus = useCallback(
    (match: ReferenceMatchResult): ReferenceMatchStatus =>
      isUnresolved(match) && hasLocalSelection(match) ? 'mapped' : match.status,
    [hasLocalSelection]
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<
      string,
      EntityCategoryCount
    > = {};
    ENTITY_ORDER.forEach((entity) => {
      const entityItems = matches.filter((match) => match.entityType === entity);
      counts[entity] = {
        mapped: entityItems.filter((match) => {
          const status = effectiveStatus(match);
          return status === 'mapped' || status === 'exact';
        }).length,
        pending: entityItems.filter(
          (match) => effectiveStatus(match) === 'pending'
        ).length,
        missing: entityItems.filter(
          (match) => effectiveStatus(match) === 'invalid_mapping'
        ).length,
      };
    });
    return counts;
  }, [matches, effectiveStatus]);

  const handleActiveEntityChange = useCallback((entity: ReferenceEntity) => {
    setActiveEntity(entity);
    setEditingKey(null);
    setEditingValue('');
  }, []);

  const handleSelectionChange = useCallback(
    (key: string, value: number | null) => {
      setSelections((current) => {
        const next = { ...current };
        if (value == null) delete next[key];
        else next[key] = value;
        return next;
      });
    },
    []
  );

  const handleEditingValueChange = useCallback((value: string) => {
    setEditingValue(value);
  }, []);

  const handleStartEdit = useCallback(
    (key: string, currentId?: number) => {
      setEditingKey(key);
      setEditingValue(String(currentId ?? ''));
      clearRowError(key);
    },
    [clearRowError]
  );

  const handleCancelEdit = useCallback(
    (key: string) => {
      setEditingKey(null);
      setEditingValue('');
      clearRowError(key);
    },
    [clearRowError]
  );

  const resolveAndImport = useCallback(async (confirmOverwrite = false) => {
    setErrorMessage(null);
    setDialogErrorMessage(null);
    setRowErrors({});

    const pending = unresolvedMatches.filter(
      (match) => match.status === 'pending'
    );
    const missing = unresolvedMatches.filter(
      (match) => match.status === 'invalid_mapping'
    );

    const missingSelections = unresolvedMatches.filter(
      (match) => !selections[selectionKey(match.entityType, match.normalizedValue)]
    );
    if (missingSelections.length) {
      const targetEntity = ENTITY_ORDER.find((entity) =>
        missingSelections.some((match) => match.entityType === entity)
      );
      if (targetEntity) setActiveEntity(targetEntity);
      const firstMissing = targetEntity
        ? missingSelections.find((match) => match.entityType === targetEntity)
        : undefined;
      const focusTarget = firstMissing ?? missingSelections[0];
      setFocusErrorKey(
        selectionKey(focusTarget.entityType, focusTarget.normalizedValue)
      );
      setRowErrors(
        Object.fromEntries(
          missingSelections.map((match) => [
            selectionKey(match.entityType, match.normalizedValue),
            'Selecione uma correspondência.',
          ])
        )
      );
      setErrorMessage('Selecione uma correspondência para todos os itens pendentes.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (pending.length) {
        try {
          await createMappings(
            pending.map((match) => ({
              entityType: match.entityType,
              externalValue: match.externalValue,
              internalId: selections[selectionKey(match.entityType, match.normalizedValue)],
            }))
          );
        } catch (error) {
          if (!applyApiErrorDetails(error, pending))
            setDialogErrorMessage(getErrorMessage(error));
          return;
        }
      }

      for (const match of missing) {
        if (!match.mappingId) continue;
        try {
          await updateMapping(
            match.mappingId,
            selections[selectionKey(match.entityType, match.normalizedValue)]
          );
        } catch (error) {
          if (!applyApiErrorDetails(error, [match]))
            setDialogErrorMessage(getErrorMessage(error));
          return;
        }
      }

      const groups = extractReferenceGroups(rows);
      const refreshed = groups.length ? await matchReferences(groups) : [];
      const stillUnresolved = refreshed.filter(isUnresolved);
      setMatches(refreshed);
      if (stillUnresolved.length) {
        setRowErrors(
          Object.fromEntries(
            stillUnresolved.map((match) => [
              selectionKey(match.entityType, match.normalizedValue),
              'Referência sem mapeamento válido.',
            ])
          )
        );
        setErrorMessage('Ainda existem referências pendentes. Revise as correspondências.');
        return;
      }

      try {
const response = confirmOverwrite
  ? await importOrders(rows, true)
  : await importOrders(rows);
        setImportedCount(response.imported);
        setUpdatedCount(response.updated ?? 0);
        setIgnoredCount(response.ignored);
        setCancelledCount(response.cancelled ?? 0);
        setStep(4);
        const createdCount = response.created ?? Math.max(response.imported - (response.updated ?? 0), 0);
        const messages = [
          createdCount > 0
            ? `${createdCount} novo(s) registro(s) enviado(s) para a quarentena.`
            : '',
          (response.updated ?? 0) > 0
            ? `${response.updated} registro(s) atualizado(s).`
            : '',
          (response.cancelled ?? 0) > 0
            ? `${response.cancelled} agendamento(s) cancelado(s) e horário liberado.`
            : '',
          response.ignored > 0
            ? `${response.ignored} ignorado(s) por data já passada.`
            : '',
        ].filter(Boolean);
        toast({
          variant: 'default',
          title: 'Sucesso',
          description: messages.join(' ') || 'Importação concluída.',
        });
      } catch (error) {
        const responseData = (error as any)?.response?.data;
        if (responseData?.code === 'OVERWRITE_CONFIRMATION_REQUIRED') {
          const thirdOrders = getErrorDetails(error)
            .map(({ field }) => field.replace(/^thirdOrder\./, ''))
            .filter(Boolean);
          setOverwriteDialogMessage(
            thirdOrders.length
              ? `Agendamento(s) já aprovado(s): ${thirdOrders.join(', ')}.`
              : getErrorMessage(error)
          );
          setOverwriteDialogOpen(true);
          return;
        }
        if (!applyApiErrorDetails(error, refreshed))
          setDialogErrorMessage(getErrorMessage(error));
      }
    } catch (error) {
      if (!applyApiErrorDetails(error, matches))
        setDialogErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    unresolvedMatches,
    selections,
    createMappings,
    updateMapping,
    rows,
    matchReferences,
    importOrders,
    toast,
    applyApiErrorDetails,
    matches,
  ]);

  return (
    <Scaffold>
      <S.MainPanel as="section">
        <HoraCertaHeader />

        <HoraCertaStepper step={step} />

        {step === 1 && (
          <HoraCertaFileStep
            inputRef={inputRef}
            onFileSelect={(selected) => void processFile(selected)}
          />
        )}

        {step === 2 && preview && (
          <HoraCertaPreviewStep
            preview={preview}
            sheetName={sheetName}
            onSheetNameChange={setSheetName}
            currentSheet={currentSheet}
            rowImportStatuses={rowImportStatuses}
          />
        )}

        {step === 3 && (
          <HoraCertaMappingStep
            activeEntity={activeEntity}
            onActiveEntityChange={handleActiveEntityChange}
            categoryCounts={categoryCounts}
            entityMatches={entityMatches}
            selections={selections}
            editingKey={editingKey}
            editingValue={editingValue}
            savingKey={savingKey}
            rowErrors={rowErrors}
            hasLocalSelection={hasLocalSelection}
            effectiveStatus={effectiveStatus}
            optionsByEntity={optionsByEntity}
            registerFieldRef={registerFieldRef}
            onSearchReference={searchReferenceOptions}
            onSelectionChange={handleSelectionChange}
            onEditingValueChange={handleEditingValueChange}
            onClearRowError={clearRowError}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onSaveMapping={saveMappingEdit}
          />
        )}

        {step === 4 && (
          <HoraCertaResultStep
            errorMessage={errorMessage}
            importedCount={importedCount}
            updatedCount={updatedCount}
            ignoredCount={ignoredCount}
            cancelledCount={cancelledCount}
            onRetry={() => {
              setErrorMessage(null);
              setStep(3);
            }}
            onReset={reset}
          />
        )}

        {errorMessage && step !== 4 && (
          <p
            role="alert"
            className="mt-4 text-sm text-red-600 whitespace-pre-line"
          >
            {errorMessage}
          </p>
        )}

        <Dialog
          open={overwriteDialogOpen}
          onOpenChange={setOverwriteDialogOpen}
        >
          <DialogContent className="bg-white sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Confirmar atualização de agendamento</DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm text-slate-700">
              {overwriteDialogMessage} Ao continuar, os dados importados, como nome e CPF do
              motorista, serão substituídos. A ordem existente e seu horário aprovado serão
              mantidos. Deseja continuar?
            </DialogDescription>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOverwriteDialogOpen(false)}
              >
                Não atualizar
              </Button>
              <Button
                type="button"
                disabled={isSubmitting}
                className="bg-[#EE7C2C] hover:bg-[#d4691f] text-white"
                onClick={() => {
                  setOverwriteDialogOpen(false);
                  void resolveAndImport(true);
                }}
              >
                {isSubmitting ? 'Atualizando...' : 'Atualizar dados'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={Boolean(dialogErrorMessage)}
          onOpenChange={(open) => {
            if (!open) setDialogErrorMessage(null);
          }}
        >
          <DialogContent className="bg-white sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Não foi possível continuar</DialogTitle>
            </DialogHeader>
            <DialogDescription className="max-h-[50vh] overflow-y-auto whitespace-pre-line text-sm text-slate-700">
              {dialogErrorMessage}
            </DialogDescription>
            <DialogFooter>
              <Button
                type="button"
                className="bg-[#EE7C2C] hover:bg-[#d4691f] text-white"
                onClick={() => setDialogErrorMessage(null)}
              >
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {step === 2 && currentSheet && !hasImportableRows && (
          <p role="alert" className="mt-6 text-right text-sm font-semibold text-red-700">
            Nenhum registro elegível para importação foi encontrado!
          </p>
        )}

        {step !== 4 && (
          <footer className="mt-8 flex justify-end items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => (step === 1 ? reset() : setStep(step - 1))}
            >
              {step === 1 ? 'Cancelar' : 'Voltar'}
            </Button>
            {step === 1 && (
              <Button
                type="button"
                disabled={!file}
                className="bg-[#EE7C2C] hover:bg-[#d4691f] text-white"
                onClick={() => file && void processFile(file)}
              >
                Próximo
              </Button>
            )}
            {step === 2 && (
              <Button
                type="button"
                disabled={isMatching || !currentSheet || !hasImportableRows}
                className="bg-[#EE7C2C] hover:bg-[#d4691f] text-white"
                onClick={() => void goToCorrespondence()}
              >
                {isMatching ? 'Carregando...' : 'Próximo'}
              </Button>
            )}
            {step === 3 && (
              <Button
                type="button"
                disabled={isSubmitting}
                className="bg-[#EE7C2C] hover:bg-[#d4691f] text-white"
                onClick={() => void resolveAndImport()}
              >
                {isSubmitting ? 'Processando...' : 'Continuar'}
              </Button>
            )}
          </footer>
        )}
      </S.MainPanel>
    </Scaffold>
  );
};
